import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MoneyPipe } from '../../pipes/money.pipe';

export type ReceiptPaymentType = 0 | 1 | 2;

export interface ReceiptItem {
  productId: string;
  productVariantId?: string | null;
  id?: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  /**
   * Qator valyutasi. Aralash savatda qatorlar har xil valyutada bo'ladi,
   * shuning uchun yorliq `ReceiptData.currency` dan emas, SHU yerdan olinadi.
   * Berilmasa — chek valyutasiga qaytadi (bir valyutali eski cheklar).
   */
  currency?: string;
}

/** Bitta valyutadagi jamlar — aralash chekda har valyuta o'z bloki bilan chiqadi. */
export interface ReceiptCurrencyTotal {
  currency: string;
  subtotal: number;
  discount: number;
  total: number;
  /** 1 birlik `currency` = shuncha to'lov valyutasi. Bir xil bo'lsa 1. */
  rate?: number;
  /**
   * Har valyuta O'Z pulida to'langanda — shu guruh uchun berilgan summa va
   * qaytim, guruh valyutasida. Faqat shu rejimda to'ldiriladi.
   */
  paid?: number;
  change?: number;
}

export interface ReceiptData {
  receiptNumber: string;
  storeName: string;
  cashierName: string;
  date: Date | string;
  items: ReceiptItem[];
  subtotal: number;
  discount: number;
  total: number;
  paymentType: ReceiptPaymentType;
  paidAmount: number;
  change: number;
  debtCustomerName: string | null;
  /** Buyurtma valyutasi — mahsulot narxlari va jami shunda. */
  currency: string;
  /**
   * Valyuta bo'yicha jamlar. Aralash savatda `subtotal`/`discount`/`total`
   * ma'nosiz bo'ladi (`$100 + 300 000` ni qo'shib bo'lmaydi), shuning uchun
   * bittadan ortiq bo'lsa chek SHU ro'yxatni chop etadi.
   */
  totalsByCurrency?: ReceiptCurrencyTotal[];
  /**
   * Har valyuta O'Z pulida to'langanmi. Shunda o'girish umuman bo'lmagan,
   * shuning uchun chekda kurs qatori bosilmaydi.
   */
  payPerCurrency?: boolean;
  /**
   * Mijoz QAYSI pulda to'lagani. Buyurtma valyutasidan farq qilishi mumkin:
   * `$100` lik molni so'mda to'lash (I7). `paidAmount` va `change` shu valyutada.
   */
  payCurrency: string;
  /**
   * 1 birlik `currency` = shuncha `payCurrency`. Valyutalar bir xil bo'lsa 1.
   * Chekda ko'rsatiladi — mijoz qanday kursda to'laganini bilishi kerak.
   */
  payRate: number;
}

@Component({
  selector: 'app-receipt',
  standalone: true,
  imports: [MoneyPipe, CommonModule, DatePipe, DecimalPipe, TranslatePipe],
  template: `
    <div id="receipt-print" class="text-sm space-y-3 py-1 receipt-print-content">
      <div
        class="text-center border-b border-dashed border-gray-300 dark:border-gray-600 pb-3 space-y-0.5"
      >
        <p class="font-bold text-base text-gray-900 dark:text-gray-100">
          {{ receipt().storeName }}
        </p>
        <p class="text-xs text-gray-500">{{ receipt().date | date: 'dd.MM.yyyy HH:mm:ss' }}</p>
        <p class="text-xs font-bold tracking-widest text-gray-800 dark:text-gray-200">
          {{ 'ORDERS.TABLE.RECEIPT' | translate }} : {{ receipt().receiptNumber }}
        </p>
        @if (receipt().cashierName) {
          <p class="text-xs text-gray-500">
            {{ 'SALES.RECEIPT.CASHIER' | translate }}: {{ receipt().cashierName }}
          </p>
        }
      </div>

      <div class="space-y-2">
        @for (item of receipt().items; track item.id ?? $index) {
          <div>
            <div class="flex justify-between gap-2">
              <span
                class="flex-1 min-w-0 truncate text-gray-800 dark:text-gray-200 font-semibold"
                >{{ item.productName }}</span
              >
              <span class="tabular-nums font-bold text-gray-900 dark:text-gray-100">{{
                itemTotal(item) | money: lineCurrency(item)
              }}</span>
            </div>
            <div class="text-[11px] text-gray-400">
              {{ item.quantity | number: '1.0-6' }} ×
              {{ item.unitPrice | money: lineCurrency(item) }}
            </div>
            @if (item.discount > 0) {
              <div class="text-[11px] text-rose-400 text-right">
                -{{ item.discount * item.quantity | money: lineCurrency(item) }}
                {{ 'SALES.DISCOUNT' | translate }}
              </div>
            }
          </div>
        }
      </div>

      <div class="border-t border-dashed border-gray-300 dark:border-gray-600 pt-3 space-y-1">
        @for (g of totals(); track g.currency) {
          @if (!$first) {
            <div
              class="border-t border-dashed border-gray-200 dark:border-gray-700 !mt-2 pt-2"
            ></div>
          }
          <div class="flex justify-between text-xs text-gray-500">
            <span>{{ 'SALES.SUBTOTAL' | translate }}</span>
            <span class="tabular-nums">{{ g.subtotal | money: g.currency }}</span>
          </div>
          @if (g.discount > 0) {
            <div class="flex justify-between text-xs text-rose-500">
              <span>{{ 'SALES.DISCOUNT' | translate }}</span>
              <span class="tabular-nums">- {{ g.discount | money: g.currency }}</span>
            </div>
          }
          <div class="flex justify-between font-bold text-sm text-gray-900 dark:text-gray-100 pt-1">
            <span>{{ 'SALES.TOTAL' | translate }}</span>
            <span class="tabular-nums">{{ g.total | money: g.currency }}</span>
          </div>
          <!-- Har valyuta o'z pulida to'langan bo'lsa — shu guruhning to'lovi. -->
          @if (g.paid !== undefined) {
            <div class="flex justify-between text-xs text-gray-500">
              <span>{{ 'SALES.PAYMENT.PAID_AMOUNT' | translate }}</span>
              <span class="tabular-nums">{{ g.paid | money: g.currency }}</span>
            </div>
            @if (g.change) {
              <div class="flex justify-between text-xs font-bold text-emerald-600">
                <span>{{ 'SALES.PAYMENT.CHANGE' | translate }}</span>
                <span class="tabular-nums">{{ g.change | money: g.currency }}</span>
              </div>
            }
          }
        }
      </div>

      <div class="border-t border-dashed border-gray-300 dark:border-gray-600 pt-3 space-y-1">
        <!--
          Mijoz buyurtma valyutasidan boshqa pulda to'lagan bo'lsa, kurs chekda
          KO'RSATILADI. Busiz mijoz nima uchun shuncha to'laganini bilmaydi va
          keyin nizo chiqadi.
        -->
        @for (g of crossRates(); track g.currency) {
          <div class="flex justify-between text-xs text-gray-500">
            <span>{{ 'SALES.PAYMENT.RATE_LABEL' | translate }}</span>
            <span class="tabular-nums">
              1 {{ g.currency }} = {{ g.rate }} {{ receipt().payCurrency }}
            </span>
          </div>
        }
        @if (crossRates().length) {
          <div class="flex justify-between text-xs text-gray-500">
            <span>{{ 'SALES.PAYMENT.TOTAL_IN_PAY_CURRENCY' | translate }}</span>
            <span class="tabular-nums">
              {{ payTotal() | money: receipt().payCurrency }}
            </span>
          </div>
        }
        <div class="flex justify-between text-xs">
          <span class="text-gray-500">{{ 'SALES.PAYMENT.TYPE' | translate }}</span>
          <span
            class="font-semibold"
            [class.text-emerald-600]="receipt().paymentType === 0"
            [class.text-blue-600]="receipt().paymentType === 1"
            [class.text-amber-600]="receipt().paymentType === 2"
            >{{ paymentLabel(receipt().paymentType) }}</span
          >
        </div>
        @if (receipt().paymentType === 0 && !perCurrencyPaid()) {
          <div class="flex justify-between text-xs text-gray-500">
            <span>{{ 'SALES.PAYMENT.PAID_AMOUNT' | translate }}</span>
            <span class="tabular-nums">{{
              receipt().paidAmount | money: receipt().payCurrency
            }}</span>
          </div>
          @if (receipt().change > 0) {
            <div class="flex justify-between text-xs font-bold text-emerald-600">
              <span>{{ 'SALES.PAYMENT.CHANGE' | translate }}</span>
              <span class="tabular-nums">{{
                receipt().change | money: receipt().payCurrency
              }}</span>
            </div>
          }
        }
        <!--
          Ism to'lov turidan QAT'IY NAZAR ko'rsatiladi: naqd va kartada ham kassir
          xaridor ismini ixtiyoriy kiritishi mumkin.
        -->
        @if (receipt().debtCustomerName) {
          <!-- Sariq faqat nasiyada — naqd savdoda ism ogohlantirish emas. -->
          <div
            class="flex justify-between text-xs"
            [class.text-amber-600]="receipt().paymentType === 2"
            [class.text-gray-500]="receipt().paymentType !== 2"
          >
            <span>{{ 'SALES.PAYMENT.CUSTOMER_NAME' | translate }}</span>
            <span class="font-semibold">{{ receipt().debtCustomerName }}</span>
          </div>
        }
      </div>
      <p
        class="text-center text-[11px] text-gray-400 border-t border-dashed border-gray-200 dark:border-gray-700 pt-2"
      >
        {{ 'SALES.RECEIPT.FOOTER' | translate }}
      </p>
    </div>
  `,
})
export class ReceiptComponent {
  receipt = input.required<ReceiptData>();

  itemTotal(item: ReceiptItem): number {
    return (item.unitPrice - item.discount) * item.quantity;
  }

  /** Qator valyutasi — berilmagan bo'lsa chek valyutasi (bir valyutali cheklar). */
  lineCurrency(item: ReceiptItem): string {
    return item.currency ?? this.receipt().currency;
  }

  /**
   * Chop etiladigan jamlar. `totalsByCurrency` berilmagan eski cheklar uchun
   * yagona blok yasaladi — shablon ikkala holatni bir xil ishlaydi.
   */
  totals = computed<ReceiptCurrencyTotal[]>(() => {
    const r = this.receipt();
    return r.totalsByCurrency?.length
      ? r.totalsByCurrency
      : [
          {
            currency: r.currency,
            subtotal: r.subtotal,
            discount: r.discount,
            total: r.total,
            rate: r.payRate,
          },
        ];
  });

  /** To'lov valyutasidan FARQ qiladigan guruhlar — kurs faqat shular uchun bosiladi. */
  crossRates = computed(() =>
    this.receipt().payPerCurrency
      ? []
      : this.totals().filter((g) => g.currency !== this.receipt().payCurrency),
  );

  /** Jami — to'lov valyutasida. Har guruh o'z kursi bilan o'giriladi. */
  payTotal = computed(() => this.totals().reduce((s, g) => s + g.total * (g.rate ?? 1), 0));

  /**
   * To'lov guruhlar ichida ko'rsatilganmi. Shunda pastdagi yagona "to'landi/qaytim"
   * qatori TAKRORLANMAYDI — bir xil pul ikki marta bosilgandek ko'rinardi.
   */
  perCurrencyPaid = computed(() => this.totals().some((g) => g.paid !== undefined));

  paymentLabel(type: ReceiptPaymentType): string {
    return ['Naqd', 'Karta', 'Nasiya'][type] ?? '';
  }
}
